/**
 * Volume Control
 *
 * Declares a single web-accessible-resource entry that includes `*.*` (every file with
 * an extension) matched to every origin with no `use_dynamic_url` — so its icons, CSS
 * and scripts are all fetchable from any page at a fixed URL.
 *
 * The icon set under assets/icon is the cleanest signal: being images they load through
 * `probeImage` with no CORS grant, so they survive a restrictive page CSP.
 *
 * Manifest verified against version 3.5.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'acglggcafiilnibeknihgglelgfafifo';

export const volumeControl: ExtensionDefinition = {
  id: ID,
  name: 'Volume Control',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'A broad *.* WAR rule exposes every named file to every origin with no dynamic URL. ' +
    'The bundled icons are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icon/icon-128.png via <img> — a bundled icon exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/icon/icon-128.png'),
    },
    {
      id: 'war-main-css',
      kind: 'web-accessible-resource',
      description: 'Fetch main.css — the extension stylesheet exposed as a static WAR.',
      weight: 0.5,
      run: () => probeFetch(ID, 'main.css'),
    },
    {
      id: 'war-capture-tab-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch captureTab.js — a vendor-named tab-capture script, a third path ' +
        'independent of the icon and stylesheet.',
      weight: 0.5,
      run: () => probeFetch(ID, 'captureTab.js'),
    },
  ],
};
